import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PRINT_FONT_OPTIONS, type PrintFontOption } from "@vet/print-core";
import { Button, Popover } from "@akuna/ui";
import { ensureCssFontFace } from "@/lib/print/fonts";
import { cn } from "@/lib/utils";

const VISIBLE_COUNT = 8;

type Props = { value: string; onChange: (fontId: string) => void; label: string; labelledBy?: string; variant?: "desktop" | "mobile" };

function FontBlock({
  font,
  active,
  onSelect,
  mobile,
}: {
  font: PrintFontOption;
  active: boolean;
  onSelect: () => void;
  mobile?: boolean;
}) {
  useEffect(() => ensureCssFontFace(font.id), [font.id]);
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={font.label}
      onClick={onSelect}
      className={cn(
        mobile ? "h-16 w-[72px] shrink-0 justify-center" : "py-2",
        "flex flex-col items-center gap-0.5 rounded-[10px] border bg-card px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active && "border-2 border-primary bg-accent",
      )}
    >
      {/* Inline style: each sample is rendered in the font it represents. */}
      <span className="text-xl leading-none" style={{ fontFamily: font.cssStack }}>
        Aa
      </span>
      <span className="max-w-full truncate text-[10.5px] text-muted-foreground">{font.label}</span>
    </button>
  );
}

export function FontPicker({ value, onChange, label, labelledBy, variant = "desktop" }: Props) {
  const { t } = useTranslation("settings");
  const visible = PRINT_FONT_OPTIONS.slice(0, VISIBLE_COUNT);
  const more = PRINT_FONT_OPTIONS.slice(VISIBLE_COUNT);
  if (variant === "mobile") {
    // Horizontal scroll row: every font reachable, no popover.
    return (
      <div
        role="group"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1"
      >
        {PRINT_FONT_OPTIONS.map((font) => (
          <FontBlock key={font.id} mobile font={font} active={font.id === value} onSelect={() => onChange(font.id)} />
        ))}
      </div>
    );
  }
  return (
    <div role="group" aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : label} className="flex flex-col gap-1.5">
      <div className="grid grid-cols-4 gap-1.5">
        {visible.map((font) => (
          <FontBlock key={font.id} font={font} active={font.id === value} onSelect={() => onChange(font.id)} />
        ))}
      </div>
      {more.length > 0 ? (
        <Popover
          trigger={
            <Button type="button" variant="outline" size="sm" className="self-start">
              {t("printEditor.body.moreFonts")}
            </Button>
          }
          content={
            <div className="grid w-[280px] grid-cols-4 gap-1.5">
              {more.map((font) => (
                <FontBlock key={font.id} font={font} active={font.id === value} onSelect={() => onChange(font.id)} />
              ))}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
